import { createFileRoute } from '@tanstack/react-router'

import { JellyIcon } from '@/components/icons'
import { BriefEmpty, BriefList, Composer, PersonRow, SectionLabel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import type { ShootStatus } from '@/components/spravce/foceni'
import { DeliveryCard, ShootHeader } from '@/components/spravce/foceni'
import { BRIEF, BRIEF_SUGGESTIONS, DELIVERY, EDITING, PEOPLE, SHOOT } from '@/mocks/spravce/foceni'

/**
 * One shoot, four moments — the brief before it, the wait after it, and the
 * podklady that end it. `prazdny` is the state her queue links to when nobody
 * has written a line yet.
 */
type ShootSearch = { stav?: 'prazdny' | 'ceka' | 'podklady' }

const STAV_VALUES = ['prazdny', 'ceka', 'podklady'] as const

/* One state marker per moment, in the app's own status vocabulary. Hers reads
   from HER side: what the photographer calls "Odevzdáno" is, for her, work that
   has just landed on her desk. */
const STATUSES: Record<'default' | 'ceka' | 'podklady', ShootStatus> = {
  default: { label: 'Potvrzeno', icon: 'check', tone: 'success' },
  ceka: { label: 'Odfoceno', icon: 'camera', tone: 'sun' },
  podklady: { label: 'Čeká na vás', icon: 'clock', tone: 'sun' },
}

export const Route = createFileRoute('/spravce/foceni/$shoot')({
  validateSearch: (search: Record<string, unknown>): ShootSearch =>
    STAV_VALUES.includes(search.stav as (typeof STAV_VALUES)[number])
      ? { stav: search.stav as (typeof STAV_VALUES)[number] }
      : {},
  head: () => ({ meta: [{ title: 'Focení · Aplikace Bitez' }] }),
  component: SpravceShoot,
})

/**
 * The správce's shoot — the screen her half of spec §7.2 lives on, and until
 * now the one thing in her app with no home: her Fronta asked her to "doplnit
 * brief" and the client detail had a button for it that led nowhere.
 *
 * Her job around a shoot is **not the shoot**. She never drives there, so the
 * arrival notes and the route are deliberately absent — they were written for
 * the photographer and they are on his screen. What is hers is the **soupiska**
 * before it and the **podklady** after it, which is why the brief IS the body of
 * this screen rather than a sub-page: her queue item is one tap from being
 * answered, with the composer already under her thumb.
 *
 * It is the same shot list the client writes into and the photographer ticks
 * off — the same `BriefList`, from the same mock — with one difference that is
 * hers alone: **"Hlavní" is her mark** (it replaced pinning), so on her side the
 * chip is a toggle instead of a label.
 *
 * The two people at the bottom are open-items B12 made usable: the photographer
 * never talks to the client, so on a shoot both of them are reachable only
 * through her, and this is the screen where that costs her a tap if it is not
 * there.
 */
function SpravceShoot() {
  const { stav } = Route.useSearch()
  const empty = stav === 'prazdny'
  const waiting = stav === 'ceka'
  const delivered = stav === 'podklady'
  const shot = waiting || delivered

  const status = STATUSES[stav === 'ceka' || stav === 'podklady' ? stav : 'default']

  return (
    <div className="flex min-h-full flex-col px-4 pt-4 pb-2">
      <Link
        href="/spravce/prehled"
        className="-ml-1 flex w-fit items-center gap-1.5 rounded-full px-1 py-1 text-[10px] font-bold tracking-widest text-ink/40 uppercase transition-colors duration-150 hover:text-ink/70"
      >
        <JellyIcon name="arrow-left" className="size-3" />
        Přehled
      </Link>

      {/* While he edits, his line in the header says so and carries the 48h
          chip — a block of its own would repeat his name two rows apart. */}
      <div className="mt-3">
        <ShootHeader shoot={SHOOT} status={status} note={waiting ? EDITING : undefined} />
      </div>

      {/* Once they land, the screen has exactly one offer, and it goes first. */}
      {delivered && (
        <div className="animate-rise-up mt-6">
          <DeliveryCard delivery={DELIVERY} />
        </div>
      )}

      {/* One wrapper carries the stretch, so on a short screen (an empty brief)
          the composer still sits at the bottom instead of halfway up it. */}
      <div className="flex-1">
        <section className="animate-rise-up mt-8" style={{ animationDelay: '60ms' }}>
          <SectionLabel>{shot ? 'Co se mělo nafotit' : 'Co nafotit'}</SectionLabel>
          <div className="mt-4">
            {empty ? (
              <BriefEmpty note="Napište, co má Tomáš nafotit. Klient sem píše taky — co označíte jako hlavní, nafotí jako první." />
            ) : (
              <BriefList groups={BRIEF} markable={!shot} />
            )}
          </div>
        </section>

        {/* B12: on a shoot she is the only line between the two of them. */}
        <section className="animate-rise-up mt-8" style={{ animationDelay: '120ms' }}>
          <SectionLabel>Lidé</SectionLabel>
          <div className="mt-3 space-y-3.5">
            {PEOPLE.map((person) => (
              <PersonRow key={person.name} person={person} />
            ))}
          </div>
        </section>
      </div>

      {/* After the shoot there is nothing left to ask for — the composer leaves
          with the moment it belonged to. */}
      {shot ? (
        <div className="pb-4" />
      ) : (
        <Composer
          suggestions={BRIEF_SUGGESTIONS}
          placeholder="Co má Tomáš nafotit?"
          attachLabel="Přiložit referenci"
        />
      )}
    </div>
  )
}
